import ElakayImage from '../../components/images/ElakayImage';
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ChevronRight, ChevronLeft, Clock, Calendar, ArrowLeft } from "lucide-react";
import Layout from "../Layout/Layout";
import { isAuthenticated } from "../auth";
import { getAllTutorials, getTutorial } from "../auth/apiHowWeDoIt";
import HowWeDoItSidebar from "../pageInside/howWeDoIt/HowWeDoItSidebar";
import HowWeDoItDocumentView from "../pageInside/howWeDoIt/HowWeDoItDocumentView";
import { normalizeHowWeDoItDocument } from "../pageInside/howWeDoIt/howWeDoItCodeDocument";
import { resolveHowWeDoItMediaUrl } from "../pageInside/howWeDoIt/howWeDoItMediaUrl";
import { formatTutorialDate } from "../pageInside/howWeDoIt/howWeDoItLabels";
import { buildTutorialSeo, useDocumentSeo } from "../pageInside/howWeDoIt/howWeDoItSeo";
import { elakayAutoText } from "../pageInside/dataElakay";
import "../pageInside/howWeDoIt/howWeDoItDesignSystem.css";
import "./howWeDoItDetail.css";

const HowWeDoItDetail = ({ infoSource }) => {
  const auth = isAuthenticated();
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isPreview = searchParams.get("preview") === "1";

  const [tutorial, setTutorial] = useState(null);
  const [prev, setPrev] = useState(null);
  const [next, setNext] = useState(null);
  const [sidebarCategories, setSidebarCategories] = useState([]);
  const [sidebarTutorials, setSidebarTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const relatedTutorials = sidebarTutorials
    .filter((item) => {
      const itemCategory = item.category?._id || item.category;
      const currentCategory = tutorial?.category?._id || tutorial?.category;
      return item.slug !== tutorial?.slug && itemCategory === currentCategory;
    })
    .slice(0, 3);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const [detail, listData] = await Promise.all([
          getTutorial(slug, { preview: isPreview }, auth?.token),
          getAllTutorials({}, auth?.token),
        ]);
        setTutorial(normalizeHowWeDoItDocument(detail.tutorial));
        setPrev(detail.prev);
        setNext(detail.next);
        setSidebarCategories(listData.categories || []);
        setSidebarTutorials((listData.tutorials || []).map(normalizeHowWeDoItDocument));
      } catch (err) {
        setError(err.message || elakayAutoText("howWeDoIt_detail_notFound", "Tutoriel introuvable."));
        setTutorial(null);
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, isPreview]);

  // seo.title / seo.description / seo.keywords, canonical, Open Graph,
  // Twitter Card and JSON-LD for this tutorial (noindex in preview or when
  // the tutorial is not public).
  useDocumentSeo(tutorial ? buildTutorialSeo(tutorial, { preview: isPreview }) : null, infoSource?.languageAPI);

  const goTo = (target) => {
    if (!target?.slug) return;
    navigate(`/how-we-do-it/${target.slug}`);
  };

  return (
    <Layout infoSource={infoSource} loading={false} SliderPunShow={false} outOffPage={false} showFilterMenu={false} enableBodyParticles={false} enableFloatingSquares={false}>
      <main className="hwdi-detail-page hwdi-scope" style={infoSource?.styleNav}>
        <div className="hwdi-detail-shell">
          {loading && (
            <div className="hwdi-detail-layout" aria-busy="true" aria-label={elakayAutoText("howWeDoIt_loading", "Chargement...")}>
              <div className="hwdi-skeleton hwdi-detail-skeleton-sidebar" />
              <div className="hwdi-detail-main">
                <span className="hwdi-skeleton" style={{ display: "block", width: "60%", height: 14, marginBottom: 18 }} />
                <span className="hwdi-skeleton" style={{ display: "block", width: "100%", height: 220, marginBottom: 18 }} />
                <span className="hwdi-skeleton" style={{ display: "block", width: "75%", height: 30, marginBottom: 12 }} />
                <span className="hwdi-skeleton" style={{ display: "block", width: "100%", height: 14, marginBottom: 8 }} />
                <span className="hwdi-skeleton" style={{ display: "block", width: "90%", height: 14 }} />
              </div>
            </div>
          )}

          {!loading && error && (
            <div className="hwdi-detail-notfound">
              <h2>{elakayAutoText("howWeDoIt_detail_notFoundTitle", "Tutoriel introuvable")}</h2>
              <p>{error}</p>
              <Link to="/how-we-do-it">{elakayAutoText("howWeDoIt_detail_backAll", "Retour a tous les tutoriels")}</Link>
            </div>
          )}

          {!loading && tutorial && (
            <div className="hwdi-detail-layout">
              <HowWeDoItSidebar categories={sidebarCategories} tutorials={sidebarTutorials} currentSlug={tutorial.slug} />

              <div className="hwdi-detail-main">
                {isPreview && <div className="hwdi-preview-banner">{elakayAutoText("howWeDoIt_detail_previewBanner", "Mode apercu - ce tutoriel n'est pas encore publie.")}</div>}

                <div className="hwdi-detail-topbar">
                  <Link to="/how-we-do-it" className="hwdi-detail-back">
                    <ArrowLeft size={15} /> {elakayAutoText("howWeDoIt_detail_back", "Retour")}
                  </Link>
                  <nav className="hwdi-breadcrumb" aria-label={elakayAutoText("howWeDoIt_detail_breadcrumbLabel", "Fil d'Ariane")}>
                    <Link to="/">{elakayAutoText("howWeDoIt_breadcrumb_home", "Home")}</Link>
                    <ChevronRight size={13} />
                    <Link to="/how-we-do-it">{elakayAutoText("howWeDoIt_mainTitle", "How We Do It in Elakay")}</Link>
                    {tutorial.category?.name && (
                      <>
                        <ChevronRight size={13} />
                        <span>{tutorial.category.name}</span>
                      </>
                    )}
                    <ChevronRight size={13} />
                    <span className="hwdi-breadcrumb-current">{tutorial.title}</span>
                  </nav>
                </div>

                <header className="hwdi-detail-header">
                  {tutorial.coverImage?.url && <ElakayImage preset="content" src={resolveHowWeDoItMediaUrl(tutorial.coverImage.url)} alt={tutorial.coverImage.alt || ""} className="hwdi-detail-cover" />}
                  {tutorial.category?.name && <span className="hwdi-detail-category-badge">{tutorial.category.name}</span>}
                  <h1>{tutorial.title}</h1>
                  {tutorial.shortDescription && <p className="hwdi-detail-description">{tutorial.shortDescription}</p>}
                  <div className="hwdi-detail-meta">
                    <span><Clock size={13} /> {tutorial.estimatedReadingTime || 1} {elakayAutoText("h_b6c935d4f3c7b2", "min")} {elakayAutoText("howWeDoIt_detail_readTime", "de lecture")}</span>
                    <span><Calendar size={13} /> {elakayAutoText("howWeDoIt_detail_updated", "Mis a jour le")} {formatTutorialDate(tutorial.updatedAt, "long")}</span>
                  </div>
                </header>

                <div className="hwdi-detail-content" lang={tutorial.language || undefined}>
                  <div className="hwdi-detail-content-inner">
                    {/* HowWeDoItDocumentView -- THE renderer shared with
                        howWeDoItEditor.jsx's live preview (spec §18): same
                        component, same design-system CSS, same
                        iframe-if-customJs logic, so this page can never
                        structurally or visually diverge from the editor's
                        own preview. */}
                    <HowWeDoItDocumentView
                      tutorial={tutorial}
                      sandboxHeight={600}
                      linkMode="navigate"
                      sandboxTitle={elakayAutoText("howWeDoIt_detail_frameTitle", "Tutorial content")}
                    />
                  </div>
                </div>

                {relatedTutorials.length > 0 && (
                  <section className="hwdi-related" aria-labelledby="hwdi-related-title">
                    <h2 id="hwdi-related-title">{elakayAutoText("howWeDoIt_detail_related", "Guides associes")}</h2>
                    <div className="hwdi-related-grid">
                      {relatedTutorials.map((item) => (
                        <Link key={item._id} to={`/how-we-do-it/${item.slug}`} className="hwdi-related-card">
                          <span>{item.category?.name || tutorial.category?.name}</span>
                          <strong>{item.title}</strong>
                          <small><Clock size={12} /> {item.estimatedReadingTime || 1} {elakayAutoText("h_b6c935d4f3c7b2", "min")}</small>
                        </Link>
                      ))}
                    </div>
                  </section>
                )}

                <div className="hwdi-detail-nav">
                  <button type="button" disabled={!prev} onClick={() => goTo(prev)}>
                    <ChevronLeft size={15} /> {prev ? prev.title : elakayAutoText("howWeDoIt_nav_previous", "Precedent")}
                  </button>
                  <Link to="/how-we-do-it" className="hwdi-detail-nav-all">
                    {elakayAutoText("howWeDoIt_nav_backAll", "Back to All Tutorials")}
                  </Link>
                  <button type="button" disabled={!next} onClick={() => goTo(next)}>
                    {next ? next.title : elakayAutoText("howWeDoIt_nav_next", "Suivant")} <ChevronRight size={15} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </Layout>
  );
};

export default HowWeDoItDetail;
